import { notification } from 'antd';
import type { ScheduleFilters } from 'shared/api';
import type {
  EducationClassWithCurriculum,
  Curriculum,
  LastSyncInfo,
  CurriculumSyncInfoResponse,
  UpdateEducationClassCurriculumBody,
  EducationPlanSchedule,
  CurriculumScheduleStatus,
  GetScheduleResponse,
  ScheduleReport,
  CurriculumItem,
} from './types';
import { appApi } from '../api-instance';

const mapScheduleFiltersToParams = (filters?: ScheduleFilters) => {
  return {
    teacherId: filters?.teacherId,
    subjectIds: filters?.subjectIds?.join(',') || undefined,
    roomIds: filters?.roomIds?.join(',') || undefined,
    educationalClassIds: filters?.educationalClassIds?.join(',') || undefined,
  };
};

export const getEducationClassesWithCurriculum = (): Promise<EducationClassWithCurriculum[]> => {
  return appApi
    .get('/education-class-curriculum')
    .then((res) => res.data)
    .catch((error) => {
      notification.error({ message: 'Не удалось загрузить классы с учебными планами' });
      throw error;
    });
};

export const getCurriculums = (): Promise<Curriculum[]> => {
  return appApi
    .get('/curriculum')
    .then((res) => res.data)
    .catch((error) => {
      notification.error({ message: 'Не удалось загрузить учебные планы' });
      throw error;
    });
};

export const getCurriculumById = (curriculumId: string): Promise<Curriculum> => {
  return appApi
    .get(`/curriculum/${curriculumId}/with-items`)
    .then((res) => res.data)
    .catch((error) => {
      notification.error({ message: 'Не удалось загрузить учебный план' });
      throw error;
    });
};

export const getCurriculumElementsById = (curriculumId: string): Promise<CurriculumItem[]> => {
  return appApi
    .get(`/curriculum/${curriculumId}`)
    .then((res) => res.data)
    .catch((error) => {
      notification.error({ message: 'Не удалось загрузить учебный план' });
      throw error;
    });
};

export const createCurriculum = (body: Omit<Curriculum, 'id' | 'source'>): Promise<Curriculum> => {
  return appApi
    .post('/curriculum', body)
    .then((res) => res.data)
    .catch((error) => {
      notification.error({ message: 'Не удалось создать учебный план' });
      throw error;
    });
};

export const updateCurriculum = (
  curriculumId: string,
  body: Omit<Curriculum, 'id' | 'source'>,
): Promise<Curriculum> => {
  return appApi
    .put(`/curriculum/${curriculumId}`, body)
    .then((res) => res.data)
    .catch((error) => {
      notification.error({ message: 'Не удалось обновить учебный план' });
      throw error;
    });
};

export const deleteCurriculum = (curriculumId: string): Promise<void> => {
  return appApi
    .delete(`/curriculum/${curriculumId}`)
    .then((res) => res.data)
    .catch((error) => {
      notification.error({ message: 'Не удалось удалить учебный план' });
      throw error;
    });
};

export const syncCurriculums = (): Promise<CurriculumSyncInfoResponse> => {
  return appApi
    .post('/curriculum/sync')
    .then((res) => res.data)
    .catch((error) => {
      notification.error({ message: 'Не удалось синхронизировать учебные планы' });
      throw error;
    });
};

export const getLastSyncInfo = (): Promise<LastSyncInfo> => {
  return appApi
    .get('/curriculum/sync/info')
    .then((res) => res.data)
    .catch((error) => {
      notification.error({ message: 'Не удалось получить информацию о последней синхронизации' });
      throw error;
    });
};

export const updateEducationClassCurriculum = (
  educationClassId: number,
  body: UpdateEducationClassCurriculumBody,
): Promise<EducationClassWithCurriculum> => {
  return appApi
    .put(`/education-class-curriculum/${educationClassId}`, body)
    .then((res) => res.data)
    .catch((error) => {
      notification.error({ message: 'Не удалось обновить класс с учебным планом' });
      throw error;
    });
};

export const getEducationPlanSchedules = (
  curriculumId: string,
): Promise<EducationPlanSchedule[]> => {
  return appApi
    .get(`/curriculum/${curriculumId}/timetables`)
    .then((res) => res.data)
    .catch((error) => {
      notification.error({
        message: 'Не удалось загрузить расписания для выбранного учебного плана',
      });
      throw error;
    });
};

export const getScheduleConfigurationTimetables = (
  configurationId: string,
): Promise<EducationPlanSchedule[]> => {
  return appApi
    .get(`/schedule-configurations/${configurationId}/timetables`)
    .then((res) => res.data)
    .catch((error) => {
      notification.error({ message: 'Не удалось загрузить расписания конфигурации' });
      throw error;
    });
};

export const generateScheduleConfiguration = (configurationId: string): Promise<void> => {
  return appApi
    .post(`/schedule-configurations/${configurationId}/generate`)
    .then((res) => res.data)
    .catch((error) => {
      notification.error({ message: 'Не удалось сгенерировать расписание конфигурации' });
      throw error;
    });
};

export const getConfigurationSchedule = (
  configurationId: string,
  scheduleId: string,
  filters?: ScheduleFilters,
): Promise<GetScheduleResponse> => {
  return appApi
    .get(`/schedule-configurations/${configurationId}/timetables/${scheduleId}`, {
      params: mapScheduleFiltersToParams(filters),
    })
    .then((res) => res.data)
    .catch((error) => {
      notification.error({ message: 'Не удалось загрузить расписание конфигурации' });
      throw error;
    });
};

export const getConfigurationScheduleStatus = (
  configurationId: string,
): Promise<CurriculumScheduleStatus> => {
  return appApi
    .get(`/schedule-configurations/${configurationId}/generation-status`)
    .then((res) => res.data)
    .catch((error) => {
      notification.error({
        message: 'Не удалось загрузить статус генерации расписания конфигурации',
      });
      throw error;
    });
};

export const getScheduleStatus = (curriculumId: string): Promise<CurriculumScheduleStatus> => {
  return appApi
    .get(`/curriculum/${curriculumId}/timetables/generate/status`)
    .then((res) => res.data)
    .catch((error) => {
      notification.error({ message: 'Не удалось загрузить статус генерации расписания' });
      throw error;
    });
};

export const getEducationPlanOop = (curriculumId: string): Promise<any> => {
  return appApi
    .get(`/curriculum/${curriculumId}/view-table`)
    .then((res) => res.data)
    .catch((error) => {
      notification.error({
        message: 'Не удалось загрузить учебный план',
      });
      throw error;
    });
};

export const getConfigurationScheduleErrors = (
  configurationId: string,
  scheduleId: string,
  filters?: ScheduleFilters,
): Promise<ScheduleReport> => {
  return appApi
    .get(`/schedule-configurations/${configurationId}/timetables/${scheduleId}/report`, {
      params: mapScheduleFiltersToParams(filters),
    })
    .then((res) => res.data)
    .catch((error) => {
      notification.error({ message: 'Не удалось загрузить отчет о расписании конфигурации' });
      throw error;
    });
};
